import {
  Card,
  CircleLoader,
  ConfirmationModal,
  Header,
  PrimaryButton,
  Space,
  Text,
  Toggle,
} from '@procurify/ui'

import { Tabs } from '@webapp/core/components/Tabs'
import { useAppRoutes, useProcurifyIntl } from '@webapp/hooks'
import { CatalogSecondaryCategoryAccessSection } from '@webapp/modules/purchasing/feature-sets/CatalogSecondaryCategoryAccessSection'
import { CatalogUserAccessSection } from '@webapp/modules/purchasing/feature-sets/CatalogUserAccessSection'
import messages from './messages'
import { useCatalogAccessSettingsView } from './useCatalogAccessSettingsView'

export const CatalogAccessSettingsView = () => {
  const intl = useProcurifyIntl()
  const { url } = useAppRoutes()

  const {
    states: {
      isLoading,
      isError,
      activeTab,
      rfoLockDefault,
      catalogPermissionEnabled,
      isCatalockSaving,
      isPermissionSaving,
      isLockAllOpen,
      isUnlockAllOpen,
    },
    commands: {
      toggleCatalock,
      togglePermission,
      openLockAll,
      confirmLockAll,
      dismissLockAll,
      openUnlockAll,
      confirmUnlockAll,
      dismissUnlockAll,
    },
  } = useCatalogAccessSettingsView()

  if (isLoading) {
    return <CircleLoader />
  }

  if (isError) {
    return (
      <Card>
        <Card.Content>
          <Text>{intl.formatMessage(messages.loadError)}</Text>
        </Card.Content>
      </Card>
    )
  }

  return (
    <Space size='large'>
      <Card>
        <Card.Content>
          <Space size='large'>
            <Space
              direction='horizontal'
              justify='space-between'
              align='center'
              size='large'
            >
              <Space.Item grow={1}>
                <Space size='small'>
                  <Header as='h4'>
                    {intl.formatMessage(messages.catalockHeading)}
                  </Header>
                  <Text>
                    {intl.formatMessage(messages.catalockDescription)}
                  </Text>
                </Space>
              </Space.Item>
              <Toggle
                yes={intl.formatMessage(messages.toggleOn)}
                no={intl.formatMessage(messages.toggleOff)}
                checked={rfoLockDefault}
                onChange={(event) => void toggleCatalock(event.target.checked)}
                aria-label={intl.formatMessage(
                  messages.catalockToggleAriaLabel
                )}
                disabled={isCatalockSaving}
              />
            </Space>

            <Space
              direction='horizontal'
              justify='space-between'
              align='center'
              size='large'
            >
              <Space.Item grow={1}>
                <Space size='small'>
                  <Header as='h5'>
                    {intl.formatMessage(messages.lockAllHeading)}
                  </Header>
                  <Text>{intl.formatMessage(messages.lockAllDescription)}</Text>
                </Space>
              </Space.Item>
              <PrimaryButton icon='lock' onClick={openLockAll}>
                {intl.formatMessage(messages.lockAllButton)}
              </PrimaryButton>
            </Space>

            <Space
              direction='horizontal'
              justify='space-between'
              align='center'
              size='large'
            >
              <Space.Item grow={1}>
                <Space size='small'>
                  <Header as='h5'>
                    {intl.formatMessage(messages.unlockAllHeading)}
                  </Header>
                  <Text>
                    {intl.formatMessage(messages.unlockAllDescription)}
                  </Text>
                </Space>
              </Space.Item>
              <PrimaryButton icon='unlock' onClick={openUnlockAll}>
                {intl.formatMessage(messages.unlockAllButton)}
              </PrimaryButton>
            </Space>
          </Space>
        </Card.Content>
      </Card>

      <Card>
        <Card.Content>
          <Space
            direction='horizontal'
            justify='space-between'
            align='center'
            size='large'
          >
            <Space.Item grow={1}>
              <Space size='small'>
                <Header as='h4'>
                  {intl.formatMessage(messages.catalogPermissionsHeading)}
                </Header>
                <Text>
                  {intl.formatMessage(messages.catalogPermissionsDescription1)}
                </Text>
                <Text>
                  {intl.formatMessage(messages.catalogPermissionsDescription2)}
                </Text>
              </Space>
            </Space.Item>
            <Toggle
              yes={intl.formatMessage(messages.toggleOn)}
              no={intl.formatMessage(messages.toggleOff)}
              checked={catalogPermissionEnabled}
              onChange={(event) => togglePermission(event.target.checked)}
              aria-label={intl.formatMessage(
                messages.catalogPermissionsToggleAriaLabel
              )}
              disabled={isPermissionSaving}
            />
          </Space>
        </Card.Content>
      </Card>

      {/* Panes hidden when permissions OFF (BHV-010). */}
      <Space.Item>
        <Tabs>
          <Tabs.TabLink to={url('CatalogItemAccessPage', { tab: 'users' })}>
            {intl.formatMessage(messages.tabUsers)}
          </Tabs.TabLink>
          <Tabs.TabLink
            to={url('CatalogItemAccessPage', { tab: 'departments' })}
          >
            {intl.formatMessage(messages.tabSecondaryCategories)}
          </Tabs.TabLink>
        </Tabs>
        {catalogPermissionEnabled ? (
          <>
            {activeTab === 'users' && <CatalogUserAccessSection />}
            {activeTab === 'departments' && (
              <CatalogSecondaryCategoryAccessSection />
            )}
          </>
        ) : (
          <Card>
            <Card.Content>
              <Text>
                {intl.formatMessage(messages.permissionsOffPlaceholder)}
              </Text>
            </Card.Content>
          </Card>
        )}
      </Space.Item>

      <ConfirmationModal
        open={isLockAllOpen}
        type='warning'
        header={intl.formatMessage(messages.lockAllModalHeader)}
        content={intl.formatMessage(messages.lockAllModalContent)}
        confirmButtonText={intl.formatMessage(
          messages.lockAllModalConfirmButton
        )}
        cancelButtonText={intl.formatMessage(messages.lockAllModalCancelButton)}
        onConfirm={async (done) => {
          // done() resets the confirm button for a retry — only wanted when
          // the save failed and the modal stayed open. On success the hook
          // has already closed the modal, and done() would set state on the
          // unmounted form.
          const didSave = await confirmLockAll()
          if (!didSave) {
            done()
          }
        }}
        handleDismiss={dismissLockAll}
      />

      <ConfirmationModal
        open={isUnlockAllOpen}
        type='warning'
        header={intl.formatMessage(messages.unlockAllModalHeader)}
        content={intl.formatMessage(messages.unlockAllModalContent)}
        confirmButtonText={intl.formatMessage(
          messages.unlockAllModalConfirmButton
        )}
        cancelButtonText={intl.formatMessage(
          messages.unlockAllModalCancelButton
        )}
        onConfirm={async (done) => {
          const didSave = await confirmUnlockAll()
          if (!didSave) {
            done()
          }
        }}
        handleDismiss={dismissUnlockAll}
      />
    </Space>
  )
}
